import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { App, Button, Image, Upload } from 'antd';
import React, { useState } from 'react';
import { ImageCropModal } from '@/components';

export interface ImageUploadPreviewProps {
  /** 图片URL值 - 单选时为string，多选时为string[] */
  value?: string | string[];
  /** 值变化回调 - 单选时为string，多选时为string[] */
  onChange?: (url: string | string[]) => void;
  /** 图片显示宽度 */
  width?: number;
  /** 图片显示高度 */
  height?: number;
  /** 裁剪比例 */
  aspect?: number;
  /** 裁剪弹窗高度 */
  cropHeight?: number;
  /** 裁剪弹窗宽度 */
  cropModalWidth?: number;
  /** cropper viewMode */
  cropViewMode?: 0 | 1 | 2 | 3;
  /** 是否禁用 */
  disabled?: boolean;
  /** 占位提示文本 */
  placeholder?: string;
  /** 是否支持多选 */
  multiple?: boolean;
  /** 最大上传数量（仅多选时有效） */
  maxCount?: number;
}

/**
 * 图片上传预览组件
 * - 支持图片上传、裁剪、预览、删除
 * - 鼠标悬浮显示操作按钮
 */
const ImageUploadPreview: React.FC<ImageUploadPreviewProps> = ({
  value,
  onChange,
  width = 240,
  height = 120,
  aspect = 2,
  cropHeight,
  cropModalWidth,
  cropViewMode,
  disabled = false,
  placeholder,
  multiple = false,
  maxCount = 9,
}) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const [cropOpen, setCropOpen] = useState(false);
  const [isHovering, setIsHovering] = useState<number>(-1);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [selectedImageFile, setSelectedImageFile] = useState<
    string | undefined
  >();

  // 标准化值处理 - 确保多选时返回数组，单选时返回字符串
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : value
        ? [value]
        : []
    : Array.isArray(value)
      ? value[0] || ''
      : value || '';

  const imageList = multiple
    ? (normalizedValue as string[])
    : [normalizedValue as string];
  const canAddMore = multiple
    ? imageList.filter((url) => url && url !== '').length < maxCount
    : false;

  // 处理文件选择
  const handleBeforeUpload = (file: File) => {
    // 多选模式下检查是否已达到最大数量
    if (multiple) {
      const currentCount = imageList.filter((url) => url && url !== '').length;
      if (currentCount >= maxCount) {
        message.warning(`最多只能上传${maxCount}张图片`);
        return false;
      }
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setSelectedImageFile(reader.result as string);
      setCropOpen(true);
    });
    reader.readAsDataURL(file);
    return false; // 阻止自动上传
  };

  const handleImageClick = (index?: number) => {
    if (disabled) return;

    if (
      multiple &&
      typeof index === 'number' &&
      imageList[index] &&
      imageList[index] !== ''
    ) {
      // 多选编辑模式：编辑指定索引的图片
      const imageUrl = imageList[index];
      convertImageToDataUrl(imageUrl)
        .then((dataUrl) => {
          setSelectedImageFile(dataUrl);
          setCropOpen(true);
        })
        .catch((error) => {
          console.error('Failed to load image for cropping:', error);
          setSelectedImageFile(imageUrl);
          setCropOpen(true);
        });
    } else if (!multiple && normalizedValue && normalizedValue !== '') {
      // 单选编辑模式
      const imageUrl = normalizedValue as string;
      convertImageToDataUrl(imageUrl)
        .then((dataUrl) => {
          setSelectedImageFile(dataUrl);
          setCropOpen(true);
        })
        .catch((error) => {
          console.error('Failed to load image for cropping:', error);
          setSelectedImageFile(imageUrl);
          setCropOpen(true);
        });
    }
    // 新增模式将通过Upload组件处理
  };

  const handleDeleteImage = (e: React.MouseEvent, index?: number) => {
    e.stopPropagation();

    if (multiple && typeof index === 'number') {
      // 多选删除指定索引的图片
      const newList = [...(normalizedValue as string[])];
      newList.splice(index, 1);
      onChange?.(newList);
    } else {
      // 单选删除
      onChange?.(multiple ? [] : '');
    }
  };

  const handlePreviewImage = (
    e: React.MouseEvent,
    imageUrl?: string,
    index?: number,
  ) => {
    e.stopPropagation();

    if (multiple && imageUrl) {
      setPreviewUrl(imageUrl);
    } else if (!multiple) {
      setPreviewUrl(normalizedValue as string);
    }
    setPreviewVisible(true);
  };

  // 将服务器图片URL转换为data URL以避免跨域问题
  const convertImageToDataUrl = async (url: string): Promise<string> => {
    try {
      // 通过fetch获取图片blob，然后转换为data URL
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit', // 不发送cookie，减少防盗链检查
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read blob'));
        reader.readAsDataURL(blob);
      });
    } catch (fetchError) {
      return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            resolve(dataUrl);
          } catch (canvasError) {
            reject(canvasError);
          }
        };

        img.onerror = (err) => {
          reject(
            new Error(
              `Both fetch and image loading failed. Original fetch error: ${err || 'Unknown error'}`,
            ),
          );
        };

        img.src = url;
      });
    }
  };

  const defaultPlaceholder =
    placeholder ||
    intl.formatMessage({
      id: 'pages-pool-member.common.clickToUpload',
      defaultMessage: '点击选择图片',
    });

  // 渲染单个图片项
  const renderImageItem = (imageUrl: string, index: number) => (
    <div
      key={index}
      style={{
        width,
        height,
        border: '1px dashed #d9d9d9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={() => handleImageClick(index)}
      onMouseEnter={() => !disabled && setIsHovering(index)}
      onMouseLeave={() => !disabled && setIsHovering(-1)}
    >
      {imageUrl && imageUrl !== '' ? (
        <>
          <Image
            src={imageUrl}
            width={width}
            height={height}
            style={{ objectFit: 'cover' }}
            preview={false}
          />
          {isHovering === index && !disabled && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
              }}
            >
              <Button
                type="text"
                icon={<EyeOutlined style={{ fontSize: 24 }} />}
                size="middle"
                style={{
                  color: 'white',
                  width: 50,
                  height: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={(e) => handlePreviewImage(e, imageUrl, index)}
              />
              <Button
                type="text"
                icon={<DeleteOutlined style={{ fontSize: 24 }} />}
                size="middle"
                style={{
                  color: 'white',
                  width: 50,
                  height: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={(e) => handleDeleteImage(e, index)}
              />
            </div>
          )}
        </>
      ) : (
        <div style={{ color: '#999', textAlign: 'center' }}>
          <div>{defaultPlaceholder}</div>
        </div>
      )}
    </div>
  );

  // 渲染添加按钮
  const renderAddButton = () => (
    <div
      style={{
        width,
        height,
        border: '1px dashed #d9d9d9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled || !canAddMore ? 'not-allowed' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        opacity: disabled || !canAddMore ? 0.6 : 1,
      }}
      onClick={() => (canAddMore || !multiple) && handleImageClick()}
    >
      <div style={{ color: '#999', textAlign: 'center' }}>
        <div>{defaultPlaceholder}</div>
      </div>
    </div>
  );

  // 渲染上传内容
  const renderUploadContent = () => {
    if (multiple) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {imageList.map((imageUrl, index) =>
            imageUrl && imageUrl !== ''
              ? renderImageItem(imageUrl, index)
              : null,
          )}
          {canAddMore && (
            <Upload
              beforeUpload={handleBeforeUpload}
              showUploadList={false}
              accept="image/*"
              disabled={disabled}
            >
              <div
                style={{
                  width,
                  height,
                  border: '1px dashed #d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                <div style={{ color: '#999', textAlign: 'center' }}>
                  <PlusOutlined style={{ fontSize: 16, marginBottom: 8 }} />
                  <div>{defaultPlaceholder}</div>
                </div>
              </div>
            </Upload>
          )}
        </div>
      );
    } else {
      const hasImage = imageList[0] && imageList[0] !== '';
      return hasImage ? (
        renderImageItem(imageList[0], 0)
      ) : (
        <Upload
          beforeUpload={handleBeforeUpload}
          showUploadList={false}
          accept="image/*"
          disabled={disabled}
        >
          <div
            style={{
              width,
              height,
              border: '1px dashed #d9d9d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              position: 'relative',
              overflow: 'hidden',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <div style={{ color: '#999', textAlign: 'center' }}>
              <PlusOutlined style={{ fontSize: 16, marginBottom: 8 }} />
              <div>{defaultPlaceholder}</div>
            </div>
          </div>
        </Upload>
      );
    }
  };

  return (
    <>
      {renderUploadContent()}

      <ImageCropModal
        open={cropOpen}
        aspect={aspect}
        cropHeight={cropHeight}
        modalWidth={cropModalWidth}
        viewMode={cropViewMode}
        onOpenChange={(open) => {
          setCropOpen(open);
          if (!open) {
            setSelectedImageFile(undefined);
          }
        }}
        onDone={(url) => {
          if (multiple) {
            const currentList = normalizedValue as string[];
            const filteredList = currentList.filter(
              (item) => item && item !== '',
            );
            const newList = [...filteredList, url];
            onChange?.(newList);
          } else {
            onChange?.(url);
          }
          setSelectedImageFile(undefined);
        }}
        initialImage={selectedImageFile}
      />

      {previewUrl && previewUrl !== '' && (
        <Image
          src={previewUrl}
          style={{ display: 'none' }}
          preview={{
            visible: previewVisible,
            onVisibleChange: setPreviewVisible,
          }}
        />
      )}
    </>
  );
};

export default ImageUploadPreview;
